import React, { useEffect, useState } from 'react'
import { useGLTF } from '@react-three/drei'
import { GLTFResult } from '../../types/GLTFResults'
import * as THREE from 'three'
import { UnsignedByteType } from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader'
import hdr from './studio.hdr'
import { PMREMGenerator } from 'three/src/extras/PMREMGenerator'

type HospitalRoomModelProps = JSX.IntrinsicElements['group'] &
  {
    floorNodeCallback?: (nodes: THREE.Object3D[]) => void,
    boundaryNodeCallback?: (nodes: THREE.Object3D[]) => void
    cameraTrackCallback?: (node: THREE.Object3D) => void
    animationState: number,
    startedMoveAnimationCallback?: (startingAnimationNumber:number) => void,
    finishedMoveAnimationCallback?: (finishingAnimationNumber:number) => void,
    canPlay: boolean,
    loadedCallback?: () => void
  }

const framerate = 25

type HospitalAnimationState = {
  animationClipNumber : number
  startFrame : number
  endFrame : number
}

function getAnimationStateTime (_state: number) : HospitalAnimationState {
  switch (_state) {
    case 0 :
      return { animationClipNumber: 0, startFrame: 5, endFrame: 100 }
    case 1 :
      return { animationClipNumber: 1, startFrame: 105, endFrame: 200 }
    case 2 :
      return { animationClipNumber: 2, startFrame: 205, endFrame: 300 }
    case 3 :
      return { animationClipNumber: 3, startFrame: 305, endFrame: 400 }
    case 4 :
      return { animationClipNumber: 4, startFrame: 405, endFrame: 500 }
    default :
      return { animationClipNumber: 0, startFrame: 5, endFrame: 5 }
  }
}

const HospitalRoomModel = React.forwardRef<any, HospitalRoomModelProps>((props, ref) => {
  const {
    scene,
    animations
  }: { scene: THREE.Scene, nodes: any, materials: any, animations: THREE.AnimationClip[] } = useGLTF('gltfs/hospRoom2_GLTF/hospRoom2.gltf') as unknown as GLTFResult
  const [mixer] = useState<THREE.AnimationMixer>(new THREE.AnimationMixer(scene))

  const [currentAnimationFrames, setCurrentAnimationFrames] = useState<HospitalAnimationState>(getAnimationStateTime(0))
  const [floorNodes] = useState<THREE.Object3D[]>([])
  const [boundaryNodes] = useState<THREE.Object3D[]>([])

  useEffect(() => {
    const clip = mixer?.clipAction(animations[0])
    clip.play()
    clip.paused = true

    scene.traverse((node: THREE.Object3D) => {
      if (node.name.includes('__floor__')) {
        floorNodes.push(node)
        return
      }
      if (node.name.includes('__collider__')) {
        boundaryNodes.push(node)
        return
      }
      if (node.name.includes('__walls__')) {
        boundaryNodes.push(node)
      }
      if (node.name === 'cameraLook_1') {
        if (props.cameraTrackCallback != null) {
          props.cameraTrackCallback(node)
        }
      }
    })

    if (props.floorNodeCallback != null) props.floorNodeCallback(floorNodes)
    if (props.boundaryNodeCallback != null) props.boundaryNodeCallback(boundaryNodes)
    if (props.loadedCallback != null) props.loadedCallback()

    return () => {
    }
  }, [])

  useEffect(() => {
    const newFrameRange = getAnimationStateTime(props.animationState)
    setCurrentAnimationFrames(newFrameRange)

    const action = mixer?.clipAction(animations[0])
    if (props.canPlay) {
      action.play()
      action.paused = false
      if (props.startedMoveAnimationCallback != null) {
        props.startedMoveAnimationCallback(props.animationState)
        document.dispatchEvent(new CustomEvent('RoomModelAnimationPlay'))
      }
    }
    mixer?.setTime(newFrameRange.startFrame / framerate)
  }, [props.animationState, props.canPlay])

  useFrame((_state, _deltaTime) => {
    if (mixer !== null) {
      const action = mixer.clipAction(animations[0])
      if (!action.paused) {
        if (mixer?.time > (currentAnimationFrames.endFrame / framerate)) {
          action.paused = true
          if (props.finishedMoveAnimationCallback != null) props.finishedMoveAnimationCallback(currentAnimationFrames.animationClipNumber)
        } else {
          mixer?.update(_deltaTime)
        }
      }
    }
  })

  // Initialise HDR Lighting
  const { gl } = useThree()
  const pmRemGenerator = new PMREMGenerator(gl)
  const loader = new RGBELoader()
  loader.setDataType(UnsignedByteType)
  pmRemGenerator.compileEquirectangularShader()

  useEffect(() => {
    loader.load(hdr, (texture) => {
      scene.environment = pmRemGenerator.fromEquirectangular(texture).texture
      texture.dispose()
      pmRemGenerator.dispose()
    })
  }, [scene, loader, pmRemGenerator])

  return (
    <group ref={ref}>
      <primitive {...props} object={scene} />
    </group>
  )
})

HospitalRoomModel.displayName = 'HospitalRoomModel'

export default HospitalRoomModel
